import { ArrowBack } from "@mui/icons-material";
import { Chip, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, To } from "react-router";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatKilometers, formatSeconds, formatTimeRange } from "~/modules/app/util/formatters";
import { MapPanelHeader } from "~/modules/map/components/MapPanel";
import { IconFieldworker } from "~/modules/plannerV2/components/IconFieldworker";

import { RouteDetailPanelHeaderFragment } from "./fragments.generated";

type RouteDetailPanelHeaderProps = RouteDetailPanelHeaderFragment & {
    backHref: To;
};

export function RouteDetailPanelHeader({
    distanceInMeters,
    durationInSeconds,
    startTime,
    endTime,
    plannerTravelGroup,
    backHref,
}: RouteDetailPanelHeaderProps) {
    const { t } = useTranslation("plannerV2");
    const ownerFieldworker = plannerTravelGroup.ownerPlannerShift?.fieldworker;
    const ownerTravelMethod = plannerTravelGroup.ownerBillingType;

    return (
        <>
            <MapPanelHeader>
                <IconButton component={Link} color="inherit" to={backHref} sx={{ marginInlineEnd: 1 }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6">{t("routeDetail.route")}</Typography>
            </MapPanelHeader>
            <Stack direction="row" columnGap={2} padding={2} justifyContent="space-between">
                <Stack>
                    <Typography>{formatKilometers(distanceInMeters)}km</Typography>
                    <Typography variant="caption">{t("routeDetail.distance")}</Typography>
                </Stack>
                <Stack>
                    <Typography>{formatSeconds(durationInSeconds)}</Typography>
                    <Typography variant="caption">{t("routeDetail.duration")}</Typography>
                </Stack>
                <Stack>
                    <Typography>{formatTimeRange(parseStringToDate(startTime), parseStringToDate(endTime))}</Typography>
                    <Typography variant="caption">{t("routeDetail.time")}</Typography>
                </Stack>
            </Stack>
            {ownerFieldworker && (
                <Stack
                    direction="row"
                    columnGap={2}
                    alignItems="center"
                    padding={2}
                    sx={theme => ({
                        borderTop: `1px solid ${theme.palette.divider}`,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <IconFieldworker variant={ownerTravelMethod === "none" ? "bike" : ownerTravelMethod} />
                    <Typography sx={{ flexGrow: 1 }}>
                        {ownerFieldworker.user.firstName} {ownerFieldworker.user.lastName}
                    </Typography>
                    <Chip label={t("routeDetail.driver")} />
                </Stack>
            )}
        </>
    );
}
